<template>
  <div class="client-actions-drawer">
    <div class="drawer-header">
      <div class="client-icon">
        <i class="fas fa-user-circle"></i>
      </div>
      <div class="client-info">
        <h3 class="client-name">{{ clientName }}</h3>
        <span class="client-id">{{ clientId }}</span>
      </div>
    </div>

    <div class="drawer-divider">
      <span>Client Actions</span>
    </div>

    <nav class="drawer-nav">
      <a
        v-for="item in menuItems"
        :key="item.id"
        :class="['drawer-item', { active: activeMenuItem === item.id }]"
        @click="handleMenuClick(item.id)"
      >
        <i :class="item.icon"></i>
        <span>{{ item.label }}</span>
        <i
          v-if="item.hasNotification"
          class="fas fa-circle notification-dot"
        ></i>
      </a>
    </nav>

    <div class="drawer-footer">
      <button class="back-btn" @click="goBackToClients">
        <i class="fas fa-arrow-left"></i>
        <span>Back to Clients</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "ClientActionsDrawer",
  props: {
    activeMenu: {
      type: String,
      required: true,
    },
    client: {
      type: Object,
      required: true,
    },
  },
  computed: {
    clientName() {
      return this.client?.name || "Client";
    },
    clientId() {
      return `CID: ${this.client?.CID || "N/A"}`;
    },
    currentRoute() {
      return this.$route.path;
    },
    activeMenuItem() {
      const pathParts = this.$route.path.split("/");
      const lastPart = pathParts[pathParts.length - 1];

      const menuIds = this.menuItems.map((item) => item.id);
      if (menuIds.includes(lastPart)) {
        return lastPart;
      }

      if (pathParts.length === 4) {
        return "overview";
      }

      return this.activeMenu;
    },
  },
  data() {
    return {
      menuItems: [
        { id: "overview", label: "Overview", icon: "fas fa-info-circle" },
        { id: "goals-summary", label: "Goals Summary", icon: "fas fa-flag" },
        {
          id: "planning",
          label: "Planning Review",
          icon: "fas fa-clipboard-check",
          hasNotification: false,
        },
        { id: "sip", label: "SIP", icon: "fas fa-chart-line" },
        { id: "goals", label: "Goals", icon: "fas fa-bullseye" },
        { id: "documents", label: "Documents", icon: "fas fa-file-alt" },
        {
          id: "transactions",
          label: "Transactions",
          icon: "fas fa-exchange-alt",
        },
        {
          id: "assets",
          label: "Assets",
          icon: "fas fa-chart-line",
        },
        {
          id: "allocations",
          label: "Asset Allocations",
          icon: "fas fa-chart-pie",
        },
      ],
    };
  },
  methods: {
    handleMenuClick(menuId) {
      const pathParts = this.$route.path.split("/");
      let basePath;

      if (pathParts.length === 3) {
        basePath = this.$route.path;
      } else {
        basePath = pathParts.slice(0, -1).join("/");
      }

      if (
        menuId === "overview" &&
        basePath.endsWith(`/client/${this.$route.params.id}`)
      ) {
        return;
      }

      this.$router.push(`${basePath}/${menuId}`);
    },
    goBackToClients() {
      this.$router.push("/admin/clients");
    },
  },
};
</script>

<style scoped>
.client-actions-drawer {
  width: 260px;
  background: #f8f9fa;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
}

.drawer-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.client-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #e7f1ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.client-icon i {
  font-size: 1.8rem;
  color: #3498db;
}

.client-info {
  overflow: hidden;
}

.client-name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.client-id {
  font-size: 0.85rem;
  color: #6c757d;
}

.drawer-divider {
  padding: 0.75rem 1.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #f1f3f5;
}

.drawer-nav {
  flex: 1;
  padding: 0.5rem 0;
  overflow-y: auto;
}

.drawer-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.5rem;
  color: #495057;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  border-left: 3px solid transparent;
}

.drawer-item:hover {
  background: #e9ecef;
  color: #3498db;
}

.drawer-item.active {
  background: #e7f1ff;
  color: #3498db;
  border-left-color: #3498db;
}

.drawer-item i {
  width: 20px;
  text-align: center;
}

.notification-dot {
  position: absolute;
  right: 1.5rem;
  font-size: 0.5rem;
  color: #4d0de2;
}

.drawer-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
}

.back-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  color: #495057;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f8f9fa;
  color: #3498db;
  border-color: #3498db;
}

@media (max-width: 768px) {
  .client-actions-drawer {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    height: auto;
  }

  .drawer-nav {
    display: flex;
    overflow-x: auto;
    padding: 0.5rem;
  }

  .drawer-item {
    flex-direction: column;
    align-items: center;
    padding: 0.75rem;
    text-align: center;
    gap: 0.5rem;
    min-width: 100px;
    border-left: none;
    border-bottom: 3px solid transparent;
  }

  .drawer-item.active {
    border-left: none;
    border-bottom: 3px solid #3498db;
  }

  .notification-dot {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }
}
</style>
